import { api } from "@/shared/api";
import { dhProfileService } from "@/entities/dh-profile/api/dhProfileService";
import type { MediaTaskStatus } from "@/shared/lib/taskStatus";

// FSD features/digital-human — 数字人口播出片(/ai-digital-human)。
// 契约锚点:gscq-api DigitalHumanController(base /api/v1/digital-human);
// flagship(生动)/longform(省)照片档 + video(视频驱动,形象 kind=video 时服务端强制)。

/** 出片档位(persona 红线:不点名厂商,只暴露语义档位)。tts=文生语音纯音频档(B5.5)。 */
export type DhMode = "flagship" | "longform" | "video" | "tts";

/** 任务状态(对齐通用 MediaTask 生命周期 + DH 终态)。 */
/** AI分身任务状态 = `media_task.status`,与海报/视频同一列同一套词汇(2026-07-16 收口)。 */
export type DhStatus = MediaTaskStatus;

/** 建任务请求体(逐字对齐后端 DigitalHumanTaskCreateRequest 10 字段)。 */
export interface DhTaskCreateRequest {
  /**
   * 批 L:确认卡上那张**正式报价单**的单号(`xb_price_quote.quote_id`)。
   *
   * ★ 后端按 `(tenant, quoteId)` 把单绑到 action_id,结算时按单兑现。
   *   不带的话这一单会落一条**无主的 MEDIA settle** —— 后端目前软着陆(打
   *   `*_task_without_quote` WARN 照常出片),前端全切完之后会改成硬拒。
   * ★ 显式声明在这里,不靠对象展开塞进去:字段名打错时**没有任何东西会红** ——
   *   请求照发、后端照收、quoteId 恒 null、WARN 照打,而 WARN 没人天天看。
   */
  quoteId?: string;
  mode: DhMode;
  /** upload_photo(上传真人照)/ preset(平台形象)。tts 纯音频档无形象(缺省)。 */
  avatarSource?: string;
  /** upload_photo → uploadAvatar 返的 key;preset → 形象 id;tts 缺省。 */
  avatarRef?: string;
  /** preset(内置音色)/ profile(声纹,B 阶段)。 */
  voiceSource: string;
  voiceRef?: string | null;
  /** 直传音频 URL(与 script 二选一)。 */
  audioUrl?: string | null;
  /** 内置音色 id(voiceSource=preset 时)。 */
  presetVoice?: string | null;
  /** 分辨率档(A:480P/720P)。 */
  resolution?: string | null;
  /** 口播文案(与 audioUrl 二选一;视频档 ≤68 字封顶 15 秒)。 */
  script?: string | null;
  /** 肖像授权勾选(upload_photo 必须 true,后端门控)。 */
  consentAcknowledged: boolean;
  /** 视频档「用原声」:抽源视频音轨克隆原声念新文案(选声纹/音色时忽略)。 */
  useOriginalVoice?: boolean;
  /** P-C 爆款蓝图引用(取用登记+档案由 backend 自动处理;formulaName 服务端解析不需上送)。 */
  blueprintId?: number | null;
  /** B5 语速(0.5~2.0,缺省=1.0;仅 script TTS 路径生效,后端超界拦)。 */
  speechRate?: number | null;
  /** 表演风格(火山 §10,仅生动版;白名单 professional|warm|energetic|calm,缺省=professional)。 */
  performanceStyle?: string | null;
  /** B6 场景卡打标(catalog sceneKey;缺省=不打标)。 */
  sceneKey?: string | null;
  /**
   * 声音系统 V2 三件套(2026-09-08,A/B/C 三批同稿契约)。
   * ★只对「我的声纹」(voiceSource=profile,克隆声线走新模型)真实生效;预置音色是旧引擎,
   *   后端对 preset 路忽略 —— 前端也只在 profile 路带,别给旧引擎塞噪声字段。
   */
  /** 语言(16 项词表,见 entities/dh-profile voiceV2Options;缺省="zh")。 */
  voiceLanguage?: string | null;
  /** 口音/方言 slug(20 项词表;null=普通话,仅 voiceLanguage=zh 可非空,后端同口径拦)。 */
  voiceDialect?: string | null;
  /** 演绎方式(natural/sales/professional/ceo/brand/expressive;缺省=natural)。 */
  voiceStyle?: string | null;
  /** 字幕开关(0908):缺省=烧(服务端默认);false=不烧。 */
  burnSubtitles?: boolean;
  /**
   * E3/W5 b-roll 逐句脚本 + 配图。★只送内部 id,**不送 objectKey**(防 SSRF 口径):
   * assetId(我的作品)与 packMaterialId(双 Pack 产品/人物物料)**二选一**,两个都带
   * 后端直接 VALIDATION_ERROR。一句都没配图时**整个字段不要送**:走原有单段合成路径。
   */
  brollLines?: { text: string; broll: boolean; assetId?: number; packMaterialId?: number }[];
  /**
   * 配乐(2026-09-07,与视频线共库)。平台曲库曲目 id —— 与 `customBgmObjectKey` 二选一,
   * 同给时服务端以 `customBgmObjectKey` 优先。两个都不给 = 不配乐(默认,存量行为不变)。
   * ★只有出视频的档位(video/flagship/longform)才带;纯音频档(tts)产物是音频,不吃 BGM。
   */
  bgmId?: number | null;
  /**
   * 自传 mp3 的 MinIO objectKey —— `videoService.uploadCustomBgm(file)` 返回的 objectKey
   * 原样带回;与 `bgmId` 二选一、同给时优先生效。成片配乐(含口播时自动压低的 ducking)
   * 全在服务端,前端只负责把这个 key 带上。
   */
  customBgmObjectKey?: string | null;
  /**
   * 配乐基线音量(2026-09-08 老板「配乐声太小」+ 加调音量按钮)。0.1~0.8,只在选了 BGM 时带;
   * 不给 = 服务端默认(0.45)。口播时仍自动压低让人声清晰,这里调的是间奏响度。
   */
  bgmVolume?: number | null;
  /**
   * 2026-10-10 追爆原话复用 §3.2:口播里留了参考片的数字/故事/品牌(黄标句),用户在出片前
   * 逐句或在确认框里确认过「可以用」→ true,落任务 settings 供售后追溯。没有黄标句不带。
   */
  refFactsConfirmed?: boolean;
}

/** 产物(DH_VIDEO 成片 / DH_AUDIO 纯音频,B5.5)。url = presign 下载/播放地址。 */
export interface DhAssetDto {
  id: number;
  status: "PENDING" | "SUCCESS" | "FAILED" | "BLOCKED";
  url?: string | null;
  /** DH_VIDEO | DH_AUDIO(老后端无此字段 → 按视频渲,兼容)。 */
  kind?: string | null;
  /**
   * 下载专用地址(后端签了 `response-content-disposition=attachment`)。
   *
   * ★与 url 分开是必须的:播放地址不能带 disposition,带了 `<video>` 会变成下载。
   * 而只有 url 时下载按钮写 `<a download>` —— 该属性对**跨域**地址被浏览器忽略
   * (成片在 MinIO 子域),于是点下载变成新标签页里播视频(0813 玉玉反馈)。
   * 老后端没有这个字段 → 回退到 url(行为与改造前一致)。
   */
  downloadUrl?: string | null;
  /** 封面帧 presign(0909 成片编辑画布底图;老后端/无 thumb 资产 → null,面板不渲染)。 */
  thumbUrl?: string | null;
}

/**
 * 任务详情(status + assets)。2026-07-07:补 name(列表卡可辨识)+ coverThumbUrl(列表封面,成片 presign)。
 * 2026-07-10:补 creatorUserId/creatorUserName(对齐 poster/video,列表卡显示创作者)。
 * 2026-07-15(三线列表对齐):补 createdAt/completedAt/isFavorite —— 用户反馈「分身卡片蛮空的」根因是
 * DTO 只有 5 个可显字段、卡片手里没牌可打,不是布局问题。completedAt 仅终态有值(非终态 null,
 * 否则会显示一个「完成于」而任务其实还在跑)。
 */
export interface DhTaskDto {
  id: number;
  name?: string | null;
  status: DhStatus;
  mode?: DhMode | null;
  errorMessage?: string | null;
  coverThumbUrl?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  isFavorite?: boolean;
  creatorUserId?: number | null;
  creatorUserName?: string | null;
  /** P-C 爆款档案:「本片套用:XXX」(非蓝图任务 null)。 */
  formulaName?: string | null;
  /** B6 场景打标(历史任务 null)。 */
  sceneKey?: string | null;
  assets?: DhAssetDto[];
  /** E1 设置回显(2026-07-27):payload 原样字段;老后端/老任务 → null,前端不显不编造。 */
  settings?: DhTaskSettings | null;
  /**
   * 有无重合成底片(0909):true = 结果页可拖字幕/改样式;false/null(老任务/老后端)=
   * 只能叠 logo(成片字幕已烧死,重烧=双层)。列表接口恒不带,详情才算。
   */
  composeBase?: boolean | null;
}

/** E1 设置回显:详情页「本次是怎么生成的」摘要(音色 id → 展示名由前端同源映射)。 */
export interface DhTaskSettings {
  script?: string | null;
  resolution?: string | null;
  speechRate?: number | null;
  voiceSource?: string | null;
  presetVoice?: string | null;
  /** 原单声纹 id(voiceSource=profile 时;老后端不回 → 换音色「没换」闸判不出就不拦)。 */
  voiceRef?: string | null;
  avatarSource?: string | null;
  useOriginalVoice?: boolean | null;
  /** 表演风格(仅生动版;老任务无键 → null)。 */
  performanceStyle?: string | null;
  /** 声音 V2 三件套回显(仅声纹路任务有;老任务/预置音色 → null,前端不显不编造)。 */
  voiceLanguage?: string | null;
  voiceDialect?: string | null;
  voiceStyle?: string | null;
  /** 字幕开关回显(2026-09-09;0908 起只在显式关时存 false,无键=服务端默认烧 → null)。 */
  burnSubtitles?: boolean | null;
}

/**
 * 成片重合成选项(0909):字段名对齐后端 VideoComposeOptions(两线同一 DTO)。
 * 全可空:undefined=不改(引擎默认/保持原值)。
 */
export interface DhComposeOptions {
  subtitleEnabled?: boolean;
  overlayConfig?: import("@/shared/lib/overlay/overlayConfig").OverlayConfig | null;
  subtitleFontScale?: string;
  subtitlePosition?: string;
  subtitleOutline?: string;
  subtitleAnchorY?: number | null;
  subtitleWidthPct?: number | null;
}

/** P-C 蓝图口播脚本逐句(broll=建议切产品画面 —— 剪辑参考标记,合成引擎不做真插播)。 */
export interface DhScriptLine {
  text: string;
  broll: boolean;
  beat?: string | null;
}

/** 1027 口播稿翻译回包。 */
export interface DhScriptTranslateResult {
  text: string;
  language: string;
  unverifiedNumbers?: string[];
}

/** C2 逐句改写回包的一行(冻结契约,字段名不许改)。 */
export interface DhBlueprintRewriteLine {
  idx: number;
  text: string;
  mode: "keep" | "swap" | "rewrite";
  broll: boolean;
  beat: string;
  added: boolean;
  unverifiedNumbers: string[];
  /** 1010 追加(可选,老 ai 不回):改写稿里用户事实撑不住的承诺 / 参考片产品名。 */
  unverifiedFacts?: string[];
}

export interface DhBlueprintRewriteRequest {
  blueprintId: number;
  productFacts: string;
  lines: { idx: number; text: string; mode: "keep" | "swap" | "rewrite"; role?: string }[];
  addProductLine: boolean;
}

export interface DhBlueprintRewriteResult {
  lines: DhBlueprintRewriteLine[];
  hints: string[];
}

export interface DhScriptResult {
  script: string;
  lines: DhScriptLine[];
}

/**
 * 声音组合试听(声音 V2)。voiceProfileId / presetVoice 二选一;text 建议取文案前 30 字,
 * 空文案用占位句。★方言/语言/演绎只对声纹真实生效(与 DhTaskCreateRequest 同口径)。
 */
export interface DhVoicePreviewRequest {
  voiceProfileId?: number | null;
  presetVoice?: string | null;
  text: string;
  voiceLanguage: string;
  voiceDialect: string | null;
  voiceStyle: string;
  speechRate?: number | null;
}

/** 任务元数据改动(PATCH /digital-human/tasks/{id})。null/缺省 = 不改该字段(后端同口径)。 */
export interface UpdateDhTaskMetaRequest {
  isFavorite?: boolean;
  name?: string;
}

export interface DhPageResponse<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

// packs W2:形象/声纹/预置资产 + 租户闸 下沉 entities/dh-profile(人物库老板 IP 流要用,
// FSD features 互不 import)。类型原名 re-export、方法同名委托 —— 本 feature 调用面零改动。
export type {
  AvatarProfileDto,
  AvatarProfileListView,
  PresetAvatarDto,
  PresetVoiceDto,
  VoiceProfileDto,
  VoiceProfileListView,
} from "@/entities/dh-profile/api/dhProfileService";

const BASE = "/digital-human";

export const digitalHumanService = {
  /** 建数字人出片任务(异步),返 taskId。 */
  createTask: (req: DhTaskCreateRequest) => api.post<{ taskId: number }>(`${BASE}/tasks`, req),

  /**
   * P-C 蓝图→口播脚本(同步,轻 LLM 秒级):backend 读蓝图 payload → ai 按「口播公式 ×
   * 用户产品事实」逐句重写 + b-roll 标记。可反复重试,不登记取用(登记在建任务派发成功后)。
   */
  blueprintScript: (blueprintId: number, userInput?: string) =>
    api.post<DhScriptResult>(`${BASE}/blueprint-script`, {
      blueprintId,
      userInput: userInput ?? "",
    }),

  /**
   * C2 逐句改写(2026-10-10 冻结契约):keep 句服务端原样返回不进 LLM;全 keep 且不加产品句
   * 不调 LLM 不计费。权益门/计费同 blueprint-script。LLM 失败 → 503 人话,前端提示重试。
   */
  blueprintLinesRewrite: (req: DhBlueprintRewriteRequest) =>
    api.post<DhBlueprintRewriteResult>(`${BASE}/blueprint-lines-rewrite`, req),

  /**
   * 1027 口播稿翻译:选了外语但稿子是中文时一键译(语言选项只让引擎用该语言朗读,不翻译稿子)。
   * 计费同 blueprint-script(dh_script 小票)。unverifiedNumbers = 原文有、译文里没找到的数字,要标黄让用户核。
   */
  scriptTranslate: (text: string, language: string) =>
    api.post<DhScriptTranslateResult>(`${BASE}/script-translate`, { text, language }),

  /**
   * 产物墙分页;status(SUCCESS/FAILED 等)/bucket(running)+ #9 组织归属(scope/departmentId/memberId)可选筛选。
   * 2026-07-15:补 q(任务名搜索,后端 name LIKE 全表搜 —— 不是前端只过滤当前页,那样翻页外的命中会漏)。
   */
  listTasks: (
    params: {
      page?: number;
      size?: number;
      status?: string;
      /** running = PENDING|RUNNING(进行中节);history = 终态(历史节)。 */
      bucket?: "running" | "history";
      scope?: string;
      departmentId?: number;
      memberId?: number;
      q?: string;
      /** B6 场景筛(catalog sceneKey;缺省=不筛)。 */
      sceneKey?: string;
      /** 1102 只看收藏(true 才下推)。 */
      favorite?: boolean;
    } = {}
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<DhPageResponse<DhTaskDto>>(`${BASE}/tasks${suffix}`);
  },

  /** 任务详情(含 assets)。 */
  getTask: (id: number) => api.get<DhTaskDto>(`${BASE}/tasks/${id}`),

  /**
   * 软删任务(连带软删全产物)。2026-07-15 三线列表对齐补齐 —— 此前分身列表卡**没有删除**
   * (海报/视频早有),后端也没这个端点。
   */
  deleteTask: (id: number) => api.delete<void>(`${BASE}/tasks/${id}`),

  /** 改任务元数据(收藏 / 重命名);对齐 posterService.updateTaskMeta。 */
  updateTaskMeta: (id: number, req: UpdateDhTaskMetaRequest) =>
    api.patch<DhTaskDto>(`${BASE}/tasks/${id}`, req),

  /** 产物列表(仅 DH_VIDEO 成片)。 */
  listAssets: (id: number) => api.get<DhAssetDto[]>(`${BASE}/tasks/${id}/assets`),

  /** 上传形象照,返 {key,url}(key 作 createTask 的 avatarRef)。 */
  uploadAvatar: (file: File): Promise<{ key: string; url: string }> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ key: string; url: string }>(`${BASE}/uploads/avatar`, form);
  },

  // ── 形象/声纹/预置资产 + 租户闸:packs W2 下沉 entities/dh-profile,同名委托 ──
  getTenantGate: dhProfileService.getTenantGate,
  listVoiceProfiles: dhProfileService.listVoiceProfiles,
  enrollVoiceProfile: dhProfileService.enrollVoiceProfile,
  deleteVoiceProfile: dhProfileService.deleteVoiceProfile,
  // ★形象四口随形象表一并退役(0915 归一批 B)——素材改挂人物,走 characterService。
  listPresetAvatars: dhProfileService.listPresetAvatars,
  listPresetVoices: dhProfileService.listPresetVoices,

  /**
   * 声音 V2 组合试听:按「音色 × 语言 × 方言 × 演绎」现合成一小段,返可播的 audioUrl。
   * 契约(A/B 批同稿):POST …/voices/preview;B 批后端在做,先按契约接线,联调后如有出入再对。
   */
  previewVoice: (req: DhVoicePreviewRequest) =>
    api.post<{ audioUrl: string }>(`${BASE}/voices/preview`, req),

  /**
   * 换音色重生成(B3):复用原任务形象+文案,换新音色建新任务,返新 taskId。
   * 声音 V2 三件套(2026-09-08):与 createTask 同口径 —— 只在 profile 路带
   * (预置音色是旧引擎,带了也被忽略,不塞噪声字段);词表单源 voiceV2Options。
   */
  /**
   * 成片重合成(0909 字幕拖动/样式 + logo,对齐视频线 compose-final):零计费纯后处理,
   * 原位覆盖成片。任务临时回 RUNNING,轮询回 SUCCESS 即新版;options 全可空=只重走默认。
   */
  composeFinal: (taskId: number, options: DhComposeOptions) =>
    api.post<void>(`${BASE}/tasks/${taskId}/compose-final`, options),

  /**
   * dh.render 规格档表(档位 × 清晰度 → variant_key)。生动版固定 omnihuman 168/秒(0926 拍板),
   * 其余档按 ai 执行口径(api `DhCreditPrecheck.variantOf`)。问价一律按这张表取 variantKey,前端不再自己映射。
   */
  getPriceVariants: () => api.get<DhPriceVariants>(`${BASE}/price-variants`),

  /** 视频档时长上限(1020 按出片通道取值;见 model/dhVideoLimits)。 */
  getVideoLimits: () => api.get<DhVideoLimitsDto>(`${BASE}/video-limits`),

  revoiceTask: (
    id: number,
    body: {
      voiceSource: string;
      voiceRef?: string;
      presetVoice?: string;
      voiceLanguage?: string;
      voiceDialect?: string | null;
      voiceStyle?: string;
    }
  ) => api.post<{ taskId: number }>(`${BASE}/tasks/${id}/revoice`, body),

  /**
   * 重试(api `POST /digital-human/tasks/{id}/retry`):按原任务 type_payload 原样复原创作请求,
   * 走创建主链**新建**一条任务(返新 taskId,原任务保留),新任务重新问价、照常扣积分。
   * 后端不限状态(只校 owner + 线别);卡上按共用 RETRYABLE 只在失败类终态出按钮。
   * 原任务若是上传音频驱动:音频 key 一次性,重试不复用,改走文案 TTS;BGM 也不复配(后端既有行为)。
   */
  retryTask: (id: number) => api.post<{ taskId: number }>(`${BASE}/tasks/${id}/retry`, {}),
};

/** `GET /digital-human/video-limits` 的回包(api DhVideoLimits.toPayload)。 */
export interface DhVideoLimitsDto {
  minSourceSec?: number;
  maxSourceSec?: number;
  maxScriptSec?: number;
  maxScriptChars?: number;
  charsPerSecond?: number;
}

/**
 * `GET /digital-human/price-variants` 的回包。键:`flagship_480P` / `flagship_720P` /
 * `flagship_1080P`(其余清晰度与缺省都落这一键,与 api `variantOf` 的 else 分支同义)/
 * `longform` / `video` / `tts`。
 */
export type DhPriceVariants = Partial<
  Record<
    "flagship_480P" | "flagship_720P" | "flagship_1080P" | "longform" | "video" | "tts",
    string
  >
>;
